import React, {useEffect, useState} from 'react';
import {
  Box,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {useSubscriptionPricing} from 'context/SubscriptionPricingContext';
import {useSubscriptionForm} from 'hooks/useSubscriptionForm';
import {ENTITY_CONFIG} from 'config/entityConfig';
import {
  getEntityLabel,
  getPricingStrategyOptions,
  getSubscriptionConfigRules,
} from 'utils/subscriptionRulesUtils';
import CustomPricingSection from './steps/step4Components/CustomPricingSection';

// Custom-selection card: drop the instructional text, keep the selector and the apply controls.
const customSectionSx = {
  '& .MuiAlert-standardInfo': {display: 'none'},
  '& .MuiCard-root': {boxShadow: 'none', border: 'none', mb: 0},
  '& .MuiCard-root .MuiCardContent-root': {p: 0},
  '& .MuiPaper-root': {boxShadow: 'none', p: 0, bgcolor: 'transparent'},
  '& .MuiTypography-h6': {fontSize: '0.9375rem'},
};

const STRATEGY_LABELS = {
  global: entityLabel => `All ${entityLabel}`,
  custom: entityLabel => `Selected ${entityLabel}`,
};

// Strategy toggle + (global discount input | custom item picker). Same state and
// handlers as PricingStrategySection / GlobalPricingSection / CustomPricingSection.
const CompactPricingSection = ({
  subscriptionType,
  moduleType,
  duration,
  country,
  pricingStrategy,
  onStrategyChange,
  isDiscountAllowed,
}) => {
  const {getGlobalPricing, updateGlobalPricing} = useSubscriptionPricing();
  const {setValue, trigger, errors} = useSubscriptionForm();

  const entityLabel = getEntityLabel(moduleType, ENTITY_CONFIG);
  const options = getPricingStrategyOptions(subscriptionType);
  const rules = getSubscriptionConfigRules(subscriptionType);
  const strategy = pricingStrategy.strategy;

  const globalDiscount = getGlobalPricing(subscriptionType)?.discountPercentage;
  const [localDiscount, setLocalDiscount] = useState(
    isDiscountAllowed ? globalDiscount || 0 : 0,
  );
  const [customKey, setCustomKey] = useState(0);

  useEffect(() => {
    const value = isDiscountAllowed ? globalDiscount || 0 : 0;
    setLocalDiscount(value);
    setValue('globalPricing.discountPercentage', value);
  }, [globalDiscount, isDiscountAllowed, setValue]);

  const handleDiscountChange = value => {
    setLocalDiscount(Math.min(Math.max(0, parseInt(value) || 0), 100));
  };

  const handleDiscountBlur = async () => {
    updateGlobalPricing(subscriptionType, {
      discountPercentage: localDiscount,
      moduleType,
    });
    setValue('globalPricing.discountPercentage', localDiscount);
    await trigger('globalPricing.discountPercentage');
  };

  const discountError = errors.globalPricing?.discountPercentage?.message;

  return (
    <Box>
      <Typography variant="caption" color="textSecondary" display="block" mb={1}>
        Apply pricing to
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={strategy || null}
        onChange={(e, next) => next && onStrategyChange(next)}
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            px: 2.5,
            fontWeight: 600,
          },
          '& .Mui-selected, & .Mui-selected:hover': {
            bgcolor: '#e3f2fd',
            color: '#1976d2',
          },
        }}>
        {options.map(option => (
          <ToggleButton key={option} value={option}>
            {STRATEGY_LABELS[option]?.(entityLabel) || option}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {!isDiscountAllowed && (
        <Typography variant="body2" color="textSecondary" mt={1.5}>
          Standard pricing - discounts are not available for this subscription type.
        </Typography>
      )}

      {strategy === 'global' && isDiscountAllowed && rules.showGlobalPricing && (
        <Box mt={2.5}>
          <TextField
            label="Discount"
            type="number"
            size="small"
            value={localDiscount}
            onChange={e => handleDiscountChange(e.target.value)}
            onBlur={handleDiscountBlur}
            inputProps={{min: 0, max: 100, step: 1}}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            error={!!discountError}
            helperText={discountError || `Applies to all ${entityLabel}`}
            sx={{width: 200}}
          />
        </Box>
      )}

      {strategy === 'custom' && rules.showCustomPricing && (
        <Box mt={2.5} sx={customSectionSx}>
          <CustomPricingSection
            key={customKey}
            subscriptionType={subscriptionType}
            moduleType={moduleType}
            duration={duration}
            country={country}
            onEntityOverrideUpdate={() => setCustomKey(prev => prev + 1)}
            isDiscountAllowed={isDiscountAllowed}
          />
        </Box>
      )}
    </Box>
  );
};

export default CompactPricingSection;
